import React, { useEffect, useRef, useState } from "react";

import {
    Image,
    Platform,
    Text,
    ScrollView,
    TouchableOpacity,
    View
} from "react-native";
import Activity from "../../models/Activity";
import SafeAreaView from "../../../specific/views/SafeAreaView";
import AppTheme from "../../utils/Theme";
import ActivityHeaderImage from "../../components/Activities/ActivityHeaderImage";
import * as Navigation from "../../../specific/utils/Navigation/Navigation";
import { getLanguage } from "../../utils/Localization/Localization";
import ActivityCellTitle from "../../components/Activities/ActivityCells/ActivityCellTitle";
import { getLocalActivities } from "../../../specific/utils/LocalStorage";
import SVGView from "../../utils/SvgView";
import { IcPlay } from "../../utils/Icons";
import Fonts from "../../Theme/Fonts";
import { ScreenActivitySheetScreen, ScreenSubscriptionPaywall } from "../../utils/Constants";
import User from "../../data/user/User";
import PremiumButton from "../../designSystem/PremiumButton";
import Alert from "../../components/Notification/Alert";
import InViewModal from "../../../specific/components/InViewModal/InViewModal";
import { analyticsEventPaywallDisplayed, propertiesEventProfile, propertiesEventSource, trackEvent } from "../../services/Tracking/Analytics";
import PremiumAdBlock from "../../components/Premium/PremiumAdBlock";
import { EventRegister } from "../../utils/EventListeners";
import { useSpecificSize } from "../../../specific/Theme/SpecificSize";

interface ActivityPlaylistProps {
    activityId: string;
    componentId: string;
    isModal?: boolean;
}

const ActivityPlaylist = (props: ActivityPlaylistProps) => {

    const appTheme: AppTheme = new AppTheme();
    const user: User = User.getInstance();

    const {
        fullScreenWidth,
        fullScreenHeight,
        availableScreenSize,
        contentSize,
        sideMenuSize,
        horizontalSpacing
    } = useSpecificSize();

    const [refresh, setRefresh] = useState<number>(0);


    const alertRef = useRef<Alert>(null);
    const inViewModal = useRef<InViewModal>(null);

    const contentWidth: number = contentSize;

    const [activity, setActivity] = useState<Activity | undefined>(undefined);
    const [localActivities, setLocalActivities] = useState<Activity[] | undefined>(undefined);

    let propsData = { ...props };
    let navigate = null;
    if (Platform.OS === "web") {
        navigate = Navigation.useNavigate();
    }

    const { activityId, componentId, isModal = false } = propsData;


    useEffect(() => {
        if (Platform.OS === "web") {
            window.scrollTo(0, 0);
        }
        EventRegister.addEventListener("userUpdated", () => {
            setRefresh(new Date().getTime());
        });

        return () => {
            EventRegister.removeEventListener("userUpdated");
        };
    }, []);

    // Activités
    useEffect(() => {
        if (localActivities === undefined) {
            getActivities();
        } else {
            // On va récupérer l'activité "mère"
            if (localActivities[activityId] !== undefined) {
                setActivity(localActivities[activityId]);
            }
        }
    }, [localActivities]);

    const getActivities = async () => {
        const localActivitiesFromStorage = await getLocalActivities();
        setLocalActivities(localActivitiesFromStorage.data);
    }

    const onShowActivityRequest = (anActivity: Activity, isPremium: boolean) => {
        if (isPremium === false) {
            if (Platform.OS === "web") {
                Navigation.showModalInView({
                    componentId,
                    navigate,
                    name: ScreenActivitySheetScreen,
                    passProps: {
                        activityId: anActivity.key,
                        referrerTitle: activity.title[getLanguage()]
                    }
                })
            } else {
                Navigation.push({
                    componentId,
                    navigate,
                    name: ScreenActivitySheetScreen,
                    passProps: {
                        activityId: anActivity.key,
                        referrerTitle: activity.title[getLanguage()]
                    }
                });
            }
        } else {
            trackEvent(analyticsEventPaywallDisplayed, { [propertiesEventSource]: "activity_playlist", [propertiesEventProfile]: user.userProfile() })
            showSubscription();
        }
    }

    const showSubscription = async () => {
        if ((user.demo !== undefined) && (user.demo === true)) {
            let title = "Compte de démonstration";
            let body = "Pour accéder aux ateliers Premium, il vous faut un abonnement. Vous pouvez en faire la demande sur le site tne.reseau-canope.fr";
            alertRef.current?.showAlert({
                title,
                body
            });
        } else {
            Navigation.showModalInView({
                componentId: componentId,
                name: ScreenSubscriptionPaywall,
                passProps: {
                    isModal: true
                }
            })
        }
    }

    const contentCell = (content: Activity): React.JSX.Element => {
        let isMediaInUserHistory = false;
        // Si le media est "locké" par défaut on doit vérifier s'il a déjà été lu par l'utilisateur
        if (user.media !== undefined) {
            if (user.media[content.category_keys[0]] !== undefined) {
                if (user.media[content.category_keys[0]].indexOf(content.key) !== -1) {
                    isMediaInUserHistory = true;
                }
            }
        }
        let playButton: React.JSX.Element = <View style={{ justifyContent: "center", alignItems: "center", backgroundColor: isMediaInUserHistory === true ? appTheme.darkBlue : appTheme.white, borderColor: appTheme.darkBlue, borderWidth: 1, width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(15) }}>
            <SVGView Component={IcPlay} color={isMediaInUserHistory === false ? appTheme.darkBlue : appTheme.white} size={appTheme.pixelPerfect(12)} />
        </View>;
        let isPremium: boolean = false;
        if ((content.access.is_premium === true) && (user.isPremium() === false)) {
            isPremium = true;
            playButton = <PremiumButton onPress={() => onShowActivityRequest(content, true)} iconSize={appTheme.pixelPerfect(18)} size={appTheme.pixelPerfect(30)} />
        }
        let bodyView: React.JSX.Element | null = null;
        if ((content.body !== undefined) && (content.body[getLanguage()] !== undefined)) {
            bodyView = <Text numberOfLines={3} style={{ flexShrink: 1, fontFamily: Fonts.primary.regular, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(9), paddingStart: appTheme.pixelPerfect(50) }}>{content.body[getLanguage()]}</Text>;
        }
        return <TouchableOpacity onPress={() => onShowActivityRequest(content, isPremium)} style={{ width: contentWidth, paddingHorizontal: appTheme.pixelPerfect(20), marginTop: appTheme.pixelPerfect(20) }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: appTheme.pixelPerfect(20), width: appTheme.getFullAppWidth() - appTheme.pixelPerfect(40) }}>
                {playButton}
                <Text style={{ flexShrink: 1, fontFamily: Fonts.primary.bold, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(10) }}>{content.title[getLanguage()]}</Text>
            </View>
            {bodyView}
        </TouchableOpacity>
    }

    const renderActivityContent = (): React.JSX.Element[] | null => {
        if (activity.content !== undefined) {
            // On va d'abord ordonner les activités
            let orderedActivities: Activity[] = [];
            for (const anActivity of activity.content) {
                let isActivityInserted: boolean = false;
                for (const anActivityIndex in orderedActivities) {
                    if (!Object.hasOwn(orderedActivities, anActivityIndex)) continue;

                    const anOrderedActivity: Activity = orderedActivities[anActivityIndex];
                    if ((anActivity.ord !== undefined) && (anOrderedActivity.ord !== undefined)) {
                        if (anActivity.ord < anOrderedActivity.ord) {
                            orderedActivities.splice(parseInt(anActivityIndex), 0, anActivity);
                            isActivityInserted = true;
                            break;
                        }
                    }
                }
                if (isActivityInserted === false) {
                    orderedActivities.push(anActivity);
                }
            }
            let activityContent: React.JSX.Element[] = [];
            for (const aContent of orderedActivities) {
                if (aContent.type === "activity") {
                    if ((localActivities !== undefined) && (localActivities[aContent.key] !== undefined)) {
                        const aChildActivity: Activity = localActivities[aContent.key];
                        activityContent.push(contentCell(aChildActivity));
                    }

                }
            }
            return activityContent;
        }
        return null;
    }

    const renderActivityPlaylistContent = (): React.JSX.Element | null => {
        if (activity === undefined) {
            return null;
        }
        return <View style={{ width: contentWidth, alignItems: "center" }}>
            <View style={{ width: contentWidth, alignItems: "center", backgroundColor: "#FFFFFF" }}>
                <ActivityCellTitle
                    title={activity.title}
                    chapo={activity.body}
                    duration={activity.duration}
                    color={appTheme.darkBlue}
                    width={contentWidth} />
                {renderActivityContent()}
                <PremiumAdBlock onPress={showSubscription} />
                <View style={{ height: appTheme.bottomInsets > 0 ? appTheme.bottomInsets + appTheme.pixelPerfect(40) : appTheme.pixelPerfect(40) }} />
            </View>
        </View>
    }

    const renderScreenContent = (): React.JSX.Element | null => {
        if (activity === undefined) {
            return null;
        }
        const activityPlaylistContent: React.JSX.Element | null = Platform.OS === "web" ? renderActivityPlaylistContent() : <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
            {renderActivityPlaylistContent()}
        </ScrollView>;
        const borderRadius: number = Platform.OS === "web" ? appTheme.pixelPerfect(20) : 0;
        return <View style={{ flex: 1, width: contentSize, alignItems: "center", borderRadius, backgroundColor: "#FFFFFF", overflow: "hidden" }}>
            <ActivityHeaderImage
                isModal={isModal}
                activity={activity}
                showButton={true}
                onCloseRequest={() => Platform.OS === "web" ? Navigation.dismissModalInView({ componentId }) : isModal === true ? Navigation.dismissModal({componentId, navigate}) : Navigation.pop({ componentId, navigate })} />
            {activityPlaylistContent}
            <Alert ref={alertRef} />
            <InViewModal ref={inViewModal} />
        </View>
    }

    if (Platform.OS === "web") {
        return <View style={[{ paddingLeft: sideMenuSize, height: fullScreenHeight, width: fullScreenWidth }]}>
            <View style={{ width: availableScreenSize, alignItems: "center", paddingTop: appTheme.pixelPerfect(20), paddingBottom: appTheme.pixelPerfect(40) }}>
                {renderScreenContent()}
            </View>
        </View>
    }

    return renderScreenContent();
}

export default ActivityPlaylist;